import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import { Project, ProjectCreateData, ProjectUpdateData, ProjectStatus, Video, VideoStatus } from '../../types/project';

// Async thunks for project operations
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getProjects();
      if (response.data?.status === 'success') {
        return response.data.data || [];
      }
      throw new Error('Failed to fetch projects');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error fetching projects:', error);
      return rejectWithValue(error.message || 'Failed to fetch projects');
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchProjectById',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiService.getProjectById(projectId);
      if (response.data?.status === 'success' && response.data.data) {
        return response.data.data;
      }
      throw new Error('Project not found');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error fetching project:', error);
      return rejectWithValue(error.message || 'Failed to fetch project');
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData: ProjectCreateData, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.createProject(projectData);
      if (response.data?.status === 'success' && response.data.data) {
        // Refresh projects list after creation
        dispatch(fetchProjects());
        return response.data.data;
      }
      throw new Error('Failed to create project');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error creating project:', error);
      return rejectWithValue(error.message || 'Failed to create project');
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (
    { projectId, updateData }: { projectId: string; updateData: ProjectUpdateData },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await apiService.updateProject(projectId, updateData);
      if (response.data?.status === 'success') {
        // Refresh the specific project
        dispatch(fetchProjectById(projectId));
        // Refresh projects list to update overview
        dispatch(fetchProjects());
        return response.data.data;
      }
      throw new Error('Failed to update project');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error updating project:', error);
      return rejectWithValue(error.message || 'Failed to update project');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.deleteProject(projectId);
      if (response.data?.status === 'success') {
        // Refresh projects list after deletion
        dispatch(fetchProjects());
        return projectId;
      }
      throw new Error('Failed to delete project');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error deleting project:', error);
      return rejectWithValue(error.message || 'Failed to delete project');
    }
  }
);

export const uploadVideosToProject = createAsyncThunk(
  'projects/uploadVideosToProject',
  async (
    { projectId, files }: { projectId: string; files: File[] },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await apiService.uploadVideosToProject(projectId, files);
      if (response.data?.status === 'success') {
        // Refresh the project to show new videos
        dispatch(fetchProjectById(projectId));
        return response.data.data;
      }
      throw new Error('Failed to upload videos');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error uploading videos:', error);
      return rejectWithValue(error.message || 'Failed to upload videos');
    }
  }
);

export const processProject = createAsyncThunk(
  'projects/processProject',
  async (projectId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.processProject(projectId);
      if (response.data?.status === 'success') {
        // Refresh the project to show processing status
        dispatch(fetchProjectById(projectId));
        return response.data.data;
      }
      throw new Error('Failed to start project processing');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error processing project:', error);
      return rejectWithValue(error.message || 'Failed to start project processing');
    }
  }
);

export const publishProject = createAsyncThunk(
  'projects/publishProject',
  async (
    { 
      projectId, 
      publishData 
    }: { 
      projectId: string; 
      publishData: {
        platforms: string[];
        customMetadata?: {[platform: string]: {title?: string; description?: string; tags?: string[]}};
      }
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await apiService.publishProject(projectId, publishData);
      if (response.data?.status === 'success') {
        // Refresh the project to show publishing status
        dispatch(fetchProjectById(projectId));
        return response.data.data;
      }
      throw new Error('Failed to publish project');
    } catch (error: any) {
      console.error('[PROJECT_SLICE] Error publishing project:', error);
      return rejectWithValue(error.message || 'Failed to publish project');
    }
  }
);

// Project state interface
interface ProjectState {
  // Projects list
  projects: Project[];
  projectsLoading: boolean;
  projectsError: string | null;
  
  // Current project
  currentProject: Project | null;
  currentProjectLoading: boolean;
  currentProjectError: string | null;
  
  // Operations
  creating: boolean;
  createError: string | null;
  updating: boolean;
  updateError: string | null;
  deleting: boolean;
  deleteError: string | null;
  
  // Video operations
  uploadingVideos: boolean;
  uploadError: string | null;
  uploadProgress: number;
  
  // Processing
  processing: boolean;
  processingError: string | null;
  
  // Publishing
  publishing: boolean;
  publishError: string | null;
  
  // Filters and UI state
  filters: {
    status?: ProjectStatus;
    contentType?: string;
    searchQuery?: string;
  };
  
  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Initial state
const initialState: ProjectState = {
  projects: [],
  projectsLoading: false,
  projectsError: null,
  
  currentProject: null,
  currentProjectLoading: false,
  currentProjectError: null,
  
  creating: false,
  createError: null,
  updating: false,
  updateError: null,
  deleting: false,
  deleteError: null,
  
  uploadingVideos: false,
  uploadError: null,
  uploadProgress: 0,
  
  processing: false,
  processingError: null,
  
  publishing: false,
  publishError: null,
  
  filters: {},
  
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

// Project slice
const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    // Clear errors
    clearErrors: (state) => {
      state.projectsError = null;
      state.currentProjectError = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
      state.uploadError = null;
      state.processingError = null;
      state.publishError = null;
    },
    
    // Set filters
    setFilters: (state, action: PayloadAction<Partial<ProjectState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {};
    },
    
    // Set pagination
    setPagination: (state, action: PayloadAction<Partial<ProjectState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Update upload progress
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    
    // Update video status in current project
    updateVideoStatus: (state, action: PayloadAction<{ videoId: string; status: VideoStatus }>) => {
      if (state.currentProject?.videos) {
        const video = state.currentProject.videos.find(v => v.id === action.payload.videoId);
        if (video) {
          video.status = action.payload.status;
        }
      }
    },
    
    // Clear current project
    clearCurrentProject: (state) => {
      state.currentProject = null;
      state.currentProjectError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch projects
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.projectsLoading = true;
        state.projectsError = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projectsLoading = false;
        state.projects = action.payload;
        state.pagination.total = action.payload.length;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.projectsLoading = false;
        state.projectsError = action.payload as string;
      });
    
    // Fetch project by ID
    builder
      .addCase(fetchProjectById.pending, (state) => {
        state.currentProjectLoading = true;
        state.currentProjectError = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.currentProjectLoading = false;
        state.currentProject = action.payload;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.currentProjectLoading = false;
        state.currentProjectError = action.payload as string;
      });
    
    // Create project
    builder
      .addCase(createProject.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.creating = false;
        state.currentProject = action.payload;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload as string;
      });
    
    // Update project
    builder
      .addCase(updateProject.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateProject.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload as string;
      });
    
    // Delete project
    builder
      .addCase(deleteProject.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.deleting = false;
        state.projects = state.projects.filter(p => p.id !== action.payload);
        if (state.currentProject?.id === action.payload) {
          state.currentProject = null;
        }
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload as string;
      });
    
    // Upload videos to project
    builder
      .addCase(uploadVideosToProject.pending, (state) => {
        state.uploadingVideos = true;
        state.uploadError = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadVideosToProject.fulfilled, (state) => {
        state.uploadingVideos = false;
        state.uploadProgress = 100;
      })
      .addCase(uploadVideosToProject.rejected, (state, action) => {
        state.uploadingVideos = false;
        state.uploadError = action.payload as string;
        state.uploadProgress = 0;
      });
    
    // Process project
    builder
      .addCase(processProject.pending, (state) => {
        state.processing = true;
        state.processingError = null;
      })
      .addCase(processProject.fulfilled, (state) => {
        state.processing = false;
      })
      .addCase(processProject.rejected, (state, action) => {
        state.processing = false;
        state.processingError = action.payload as string;
      });
    
    // Publish project
    builder
      .addCase(publishProject.pending, (state) => {
        state.publishing = true;
        state.publishError = null;
      })
      .addCase(publishProject.fulfilled, (state) => {
        state.publishing = false;
      })
      .addCase(publishProject.rejected, (state, action) => {
        state.publishing = false;
        state.publishError = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearErrors,
  setFilters,
  clearFilters,
  setPagination,
  setUploadProgress,
  updateVideoStatus,
  clearCurrentProject,
} = projectSlice.actions;

// Export reducer
export default projectSlice.reducer; 